import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMAuthCallback() {
  const navigate = useNavigate();
  const { setUser, setToken } = useCRMAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // New flow: custom Markeemix Google OAuth → backend already created the
        // CRM session and 302-redirected here with ?token=<crm_session_token>.
        const queryParams = new URLSearchParams(window.location.search);
        const directToken = queryParams.get('token');

        if (directToken) {
          // Store the CRM token and fetch the user via /api/crm/auth/me
          localStorage.setItem('crm_token', directToken);
          setToken(directToken);

          const meRes = await fetch(`${API}/api/crm/auth/me`, {
            headers: { 'Authorization': `Bearer ${directToken}` },
          });

          if (!meRes.ok) {
            throw new Error('Failed to load CRM user');
          }

          const meData = await meRes.json();
          if (meData.user) {
            setUser(meData.user);
          }

          toast.success('Login successful!');
          navigate('/crm/dashboard', { replace: true });
          return;
        }

        // Legacy fallback: session_id in hash from the old Emergent Auth flow
        const hash = window.location.hash;
        const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

        if (!sessionId) {
          setError('No session ID found. Please try logging in again.');
          toast.error('Login failed - no session ID');
          setTimeout(() => navigate('/crm/login'), 2000);
          return;
        }

        // Exchange session_id for user data via backend
        const response = await fetch(`${API}/api/crm/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Authentication failed');
        }

        const data = await response.json();

        // Store token and user
        if (data.token) {
          localStorage.setItem('crm_token', data.token);
          setToken(data.token);
        }
        if (data.user) {
          setUser(data.user);
        }

        toast.success('Login successful!');
        // Navigate to dashboard
        navigate('/crm/dashboard', { replace: true });
      } catch (err) {
        setError(err.message || 'Authentication failed. Please try again.');
        toast.error(err.message || 'Login failed');
        setTimeout(() => navigate('/crm/login'), 3000);
      }
    };

    processAuth();
  }, [navigate, setToken, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg mb-2">Login Failed</p>
          <p className="text-gray-400 text-sm">{error}</p>
          <p className="text-gray-500 text-xs mt-4">Redirecting to login page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">Signing you in...</p>
      </div>
    </div>
  );
}
